
import Vue from 'vue';
import LabelChip from '@/components/LabelChip.vue';
import {
  Label,
  LabelWithEdges,
  LabelSuggestion,
  LabelWithStateAndSuggestions,
  LabelSuggestionType,
  MlSuggestion,
  isMlSuggestion,
  Glasses,
  LabelPickerAdvancedLabelStatus,
} from '@/model';
import {
  groupBy, notEmpty, sortByExplicit,
} from '@/util/array-util';
import labels, {
  AdvancedLabelTypeShortcuts,
  findLabelSuggestion,
  findRelatedTo,
  getAlwaysInContextTypes,
  getContextRelationTypes,
  getDefaultActiveFilters,
  getLabelIconForGlassesType,
  getLabelTypeOrder,
  getUseContext,
  getUseGlasses,
  isNewlyAdded,
  labelSorter,
  searchLabels,
} from '@/store/labels';
import { autoAddSpecForType } from '@/store/ui-config';
import deepEqual from '@/util/deep-equals';

enum SortingOption {
  ConfidenceSuggestionName = 'confidence + suggestion + name',
  Name = 'name',
}

type LabelGroup = { group: string; members: readonly LabelWithStateAndSuggestions[] };

type LabelTypeMetadata = { icon: string; confidence?: number };

interface LabelFilter {
  readonly name: string;
  readonly icon?: string;
  readonly invertedIcon?: boolean;
  readonly numLabels?: () => number;
  readonly exampleColor?: string;
  readonly exampleLabel?: Label;
  readonly isActive?: boolean;
  readonly metadata?: LabelTypeMetadata[];
  isCoveringLabel: (label: LabelWithStateAndSuggestions) => boolean;
}

export default Vue.extend({
  name: 'LabelPickerAdvancedFilters',
  components: { LabelChip },

  props: {
    value: { type: Array as () => Label[], required: true },
    ingestSuggestions: { type: Array as () => number[], default: () => [] },
    documentLabels: { type: Array as () => number[], default: () => [] },
    intersectingLabels: { type: Array as () => number[], default: () => [] },
    isBusy: { type: Boolean, default: false },
    mlSuggestions: { type: Map as any as () => Map<number, MlSuggestion> },
    contextLabels: { type: Array as () => Label[], default: () => [] },
    labelStatus: { type: Object as () => LabelPickerAdvancedLabelStatus, required: true },
    currentSelection: { type: Array as () => number[], required: true },
  },

  data() {
    return {
      searchInput: undefined as string | undefined,
      searchTerm: undefined as string | undefined,
      searchDebounceTimer: undefined as number | undefined,
      labelFiltersActive: [] as Array<LabelFilter>,
      sortingOn: SortingOption.ConfidenceSuggestionName as string,
      showOutOfContextLabels: true,
      glassesPanels: undefined as number[] | undefined,
    };
  },

  mounted() {
    this.applyDefaultFilters();
    this.focusSearchInput();
    document.addEventListener('keydown', this.shortcuts);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.shortcuts);
    window.clearTimeout(this.searchDebounceTimer);
  },

  watch: {
    searchInput(to: string | undefined) {
      window.clearTimeout(this.searchDebounceTimer);
      if (!to) {
        this.searchTerm = to;
        return;
      }
      this.searchDebounceTimer = window.setTimeout(() => {
        this.searchTerm = to;
      }, 250);
    },
    value: {
      handler(to, from) {
        // Only pull focus back to the search box if the user is already searching
        // in it. Otherwise async suggestion loads would steal focus from e.g. the
        // fragment assistant while the user is typing there.
        if (!deepEqual(to, from) && this.isSearchInputFocused()) {
          this.$nextTick(() => {
            this.focusSearchInput();
          });
        }
      },
    },
    options: {
      handler(to, from) {
        if (!deepEqual(to, from)) {
          this.$emit('input', to);
        }
      },
    },
    glassesPanels: {
      handler() {
        this.focusSearchInput();
      },
    },
  },

  computed: {
    useGlasses(): boolean {
      return getUseGlasses();
    },
    useContext(): boolean {
      return getUseContext();
    },
    glasses(): { group: string; icon: string; members: ReadonlyArray<Glasses> }[] {
      const contextGlassesTypes = getContextRelationTypes();
      const glasses = [...labels.getters.glasses.values()]
        .filter((g) => !contextGlassesTypes.includes(g.type) || this.currentSelection.includes(g.id));

      const opts = Array.from(
        groupBy(
          glasses,
          (label) => (label.type),
        ),
      ).map(([group, members]) => ({
        group,
        icon: getLabelIconForGlassesType(group),
        members: [...members].sort((a, b) => a.code.localeCompare(b.code)).map((value) => ({
          group,
          value,
          isActive: !!labels.state.activeGlasses.find((active) => active.type === group && active.code === value.code),
        })),
      }));
      const glassesSorterFun = sortByExplicit<{ group: string; icon: string; members: ReadonlyArray<Glasses>}, string>((a) => a.group, getLabelTypeOrder());
      opts.sort(glassesSorterFun);
      return opts;
    },
    isNotDefaultInput(): boolean {
      return (this.searchInput && this.searchInput.length > 0)
        || this.labelFiltersActive
          .filter((lf) => getDefaultActiveFilters().includes(lf.name))
          .length !== getDefaultActiveFilters().filter((f) => this.labelFilters.some((lf) => lf.name === f)).length
        || (this.useContext && !this.showOutOfContextLabels)
        || this.sortingOn !== SortingOption.ConfidenceSuggestionName;
    },
    labelFilters(): LabelFilter[] {
      return [...this.staticFilters, ...this.labelTypeFiltersToShow.flat()];
    },
    searchedAvailable(): readonly LabelWithStateAndSuggestions[] {
      if (this.searchTerm) {
        const searchResults = searchLabels(this.available, this.searchTerm);

        return searchResults.map((label) => this.toLabelWithSuggestion(this.toLabelWithEdges(label.id)),
        );
      } else {
        return this.available.map((l) => this.toLabelWithSuggestion(this.toLabelWithEdges(l.id)));
      }
    },
    numOutOfContext(): number {
      return this.searchedAvailable
        .filter((label) => (findLabelSuggestion(label, 'outofcontext')))
        .length;
    },
    exclusionFilteredOptions(): Array<LabelWithStateAndSuggestions> {
      const labelTypeSorterFun = sortByExplicit<Label, string>((a) => a.type, getLabelTypeOrder());
      const glassesLabelFilter = (l: LabelWithEdges, glassesLabel: Label) => {
        const edgeOfCorrectGroup = l.edges?.filter((edgeLabel) => edgeLabel.type === glassesLabel.type);
        if (edgeOfCorrectGroup && edgeOfCorrectGroup.length > 0) {
          return !!edgeOfCorrectGroup.find((eocg) => eocg.code === glassesLabel.code);
        } else {
          return true;
        }
      };
      return this.searchedAvailable
        .filter((label) => (labels.state.activeGlasses.length > 0 ? !labels.state.activeGlasses.find((glassesLabel) => !glassesLabelFilter(label, glassesLabel)) : true))
        .filter((label) => (this.showOutOfContextLabels || !findLabelSuggestion(label, 'outofcontext') || this.isLabelSuggestion(label)))
        .sort(labelTypeSorterFun);
    },
    available(): readonly Label[] {
      return labels.state.labels.filter((l) => l.selectable) || [];
    },
    relatedSuggestionIds(): Set<number> {
      const selectedIds = new Set(this.currentSelection);
      const suggestCodes = new Set(this.currentSelection
        .map((id) => labels.getters.lookup.get(id))
        .filter(notEmpty)
        .flatMap((l) => autoAddSpecForType(l.type).suggest));
      if (suggestCodes.size === 0) {
        return new Set<number>();
      }
      return new Set(labels.state.labels
        .filter((l) => suggestCodes.has(l.code) && !selectedIds.has(l.id))
        .map((l) => l.id));
    },
    numLabels(): number {
      return this.value.length;
    },
    numRecentlyUsed(): number {
      return this.value.filter((l) => this.labelStatus.recentlyUsed?.includes(l.id)).length;
    },
    numPinned(): number {
      return this.value.filter((l) => this.labelStatus.pinned?.includes(l.id)).length;
    },
    newlyAdded(): Label[] {
      return labels.state.labels.filter(isNewlyAdded);
    },
    numSuggestions(): number {
      return this.value
        .filter((l) => findLabelSuggestion(l, 'ingest') || findLabelSuggestion(l, 'ml'))
        .length;
    },
    staticFilters(): LabelFilter[] {
      const staticFilters: LabelFilter[] = [
        {
          name: 'Recently used',
          icon: 'mdi-history',
          isCoveringLabel: ((label) => !!this.labelStatus.recentlyUsed?.includes(label.id)),
          numLabels: () => this.numRecentlyUsed,
        },

        {
          name: 'Suggestions',
          icon: 'mdi-lightbulb-on-outline',
          invertedIcon: true,
          numLabels: () => this.numSuggestions,
          isCoveringLabel: (label) => !!(findLabelSuggestion(label, 'ml') || findLabelSuggestion(label, 'ingest') || findLabelSuggestion(label, 'related')),
        },
      ];
      if (this.labelStatus.pinned && this.labelStatus.pinned.length > 0) {
        staticFilters.unshift({
          name: 'Pinned',
          icon: 'mdi-pin',
          isCoveringLabel: ((label) => !!this.labelStatus.pinned?.includes(label.id)),
          numLabels: () => this.numPinned,
        });
      }
      if (this.newlyAdded.length > 0) {
        staticFilters.push({
          name: 'Newly added',
          icon: 'mdi-new-box',
          isCoveringLabel: ((label) => !!this.newlyAdded.find((na) => na.id === label.id)),
          numLabels: () => this.newlyAdded.length,
        });
      }

      return staticFilters.map((lf) => ({ ...lf, isActive: !!this.labelFiltersActive.find((lfa) => lfa.name === lf.name) }));
    },
    labelTypeFiltersToShow(): LabelFilter[][] {
      return this.labelTypeFilters.map((arr) => arr.filter((lf) => lf.numLabels && lf.numLabels() > 0));
    },
    labelTypeFilters(): LabelFilter[][] {
      const typeFilters: LabelFilter[] = getLabelTypeOrder().map((type) => ({
        name: type,
        isCoveringLabel: ((label) => label.type === type),
        isActive: !!this.labelFiltersActive.find((lfa) => lfa.name === type),
        numLabels: () => this.numLabelsByType(type),
        exampleColor: this.exampleColorForLabelType(type),
        exampleLabel: this.exclusionFilteredOptions.find((lg) => lg.type === type),
        metadata: this.labelGroupMetadataByName(type),
      }));
      return [typeFilters];
    },
    options(): Array<LabelGroup> {
      let memberSorterFun = (a: LabelWithStateAndSuggestions, b: LabelWithStateAndSuggestions): number => a.code.localeCompare(b.code);

      if (this.sortingOn === SortingOption.ConfidenceSuggestionName) {
        memberSorterFun = advancedLabelSorter;
      }
      const labelFiltersActiveToShow = this.labelFiltersActive.filter((lf) => this.labelFilters.find((l) => lf.name === l.name));
      // While free-text searching, ignore the default filters (so search matches
      // aren't hidden by e.g. Workflow) but still honour any filter the user
      // explicitly activated on top of them.
      const searching = !!this.searchTerm && this.searchTerm.length > 0;
      const effectiveFilters = searching
        ? labelFiltersActiveToShow.filter((lf) => !getDefaultActiveFilters().includes(lf.name))
        : labelFiltersActiveToShow;
      const opts = Array.from(
        groupBy(
          this.exclusionFilteredOptions
            .filter((label) => (effectiveFilters.length > 0
              ? effectiveFilters.find((lf) => lf.isCoveringLabel(label))
              : true)),
          (label) => (label.type),
        ),
      ).map(([group, members]) => ({ group, members: [...members].sort(memberSorterFun) }));
      const labelGroupSorterFun = sortByExplicit<LabelGroup, string>((a) => a.group, getLabelTypeOrder());
      opts.sort(labelGroupSorterFun);
      return opts;
    },

  },

  methods: {
    exampleColorForLabelType(type: string): string {
      const a = this.exclusionFilteredOptions.find((lg) => lg.type === type);
      return a ? `rgba(${a.color.r}, ${a.color.g}, ${a.color.b}, 1)` : '';
    },
    numLabelsByType(type: string): number {
      return this.exclusionFilteredOptions.filter((lg) => lg.type === type).length;
    },
    labelGroupMetadataByName(name: string): LabelTypeMetadata[] {
      const grp = this.exclusionFilteredOptions.filter((g) => g.type === name);
      return grp ? this.labelGroupMetadata(grp) : [];
    },
    labelGroupMetadata(ls: LabelWithStateAndSuggestions[]): LabelTypeMetadata[] {
      const metadata = [
        {
          count: ls.filter((l) => findLabelSuggestion(l, 'ml')).length,
          confidence: ls
            .map((l) => findLabelSuggestion(l, 'ml')?.confidence)
            .filter(notEmpty)
            .reduce((prev, curr) => (curr > prev ? curr : prev), 0),
          icon: 'mdi-lightbulb-on-outline',
        },
        {
          count: ls.filter((l) => findLabelSuggestion(l, 'ingest')).length,
          icon: 'mdi-brain',
        },
      ].filter((m) => m.count > 0);
      return metadata;
    },
    ingestLabelSuggestion(id: number): LabelSuggestion | undefined {
      return this.ingestSuggestions.includes(id) ? { type: 'ingest' } : undefined;
    },
    mlLabelSuggestion(id: number): LabelSuggestion | undefined {
      return this.mlSuggestions.get(id);
    },
    relatedLabelSuggestion(id: number): LabelSuggestion | undefined {
      return this.relatedSuggestionIds.has(id) ? { type: 'related' } : undefined;
    },
    contextLabelsById(id: number): Label | undefined {
      return this.contextLabels.find((pl) => pl.id === id);
    },
    isLabelSuggestion(label: LabelWithStateAndSuggestions): boolean {
      return !!label.suggestions?.find((s) => s.type === 'ingest' || s.type === 'ml' || s.type === 'related');
    },
    toLabelWithEdges(id: number): LabelWithEdges {
      const l = labels.getters.lookupWithEdges.get(id) ?? {
        id,
        code: `unknown label ${id}`,
        type: 'unknown',
        color: { r: 0, b: 0, g: 0 },
        selectable: true,
      };
      return l;
    },
    toLabelWithSuggestion(l: LabelWithEdges): LabelWithStateAndSuggestions {
      const relatedTo = findRelatedTo(l, [...this.labelStatus.parents, ...labels.state.activeGlasses])
        .map((li) => ({
          ...li,
          isRemoved: !!this.labelStatus.removed.find((r) => li.label.id === r.labelId),
        }));
      const oocType: LabelSuggestionType = 'outofcontext';
      const suggs = [
        this.ingestLabelSuggestion(l.id),
        this.mlLabelSuggestion(l.id),
        this.relatedLabelSuggestion(l.id),
        (this.useContext && !this.contextLabelsById(l.id) && !getAlwaysInContextTypes().includes(l.type)) ? { type: oocType } : undefined,
      ].filter(notEmpty);

      const lc: LabelWithStateAndSuggestions = ({
        ...l,
        isDocumentLabel: this.documentLabels.includes(l.id),
        isIntersectingLabel: this.intersectingLabels.includes(l.id),
        isRecentlyUsed: this.labelStatus.recentlyUsed?.includes(l.id),
        isPinned: this.labelStatus.pinned?.includes(l.id),
        isSelected: [...this.labelStatus.added.map((la) => la.labelId), ...this.labelStatus.saved.map((la) => la.labelId)].includes(l.id),
        relatedTo: relatedTo.length > 0 ? relatedTo : undefined,
        suggestions: suggs,
      });
      return lc;
    },
    toggleFilter(name: string, e: MouseEvent | undefined) {
      const addFilter = e && (e.ctrlKey || e.shiftKey);
      const labelFilter = this.labelFilters.find((lf) => lf.name === name);
      if (!labelFilter) {
        return;
      }
      if (!this.labelFiltersActive) {
        this.labelFiltersActive = [labelFilter];
      } else if (this.labelFiltersActive.find((lf) => lf.name === name)) {
        if (addFilter) {
          this.labelFiltersActive = this.labelFiltersActive.filter((lf) => lf.name !== name);
        } else if (this.labelFiltersActive.length > 1) {
          this.labelFiltersActive = [labelFilter];
        } else {
          this.labelFiltersActive = [];
        }
      } else if (addFilter) {
        this.labelFiltersActive = this.labelFiltersActive.concat(labelFilter);
      } else {
        this.labelFiltersActive = [labelFilter];
      }
      this.focusSearchInput();
    },
    focusSearchInput(force = false) {
      // Don't yank focus away from another text field (e.g. the fragment assistant
      // input) when suggestion loads / filter changes trigger a re-focus. Explicit
      // user actions (Ctrl+F, reset) pass force to override this.
      if (!force) {
        const active = document.activeElement as HTMLElement | null;
        const searchEl = (this.$refs.searchInput as any)?.$el as HTMLElement | undefined;
        if (active && !searchEl?.contains(active)
          && (active.isContentEditable || active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
          return;
        }
      }
      (this.$refs.searchInput as any).focus();
    },
    isSearchInputFocused(): boolean {
      const el = (this.$refs.searchInput as any)?.$el as HTMLElement | undefined;
      return !!el && el.contains(document.activeElement);
    },
    applyDefaultFilters() {
      const defaultFilter = getDefaultActiveFilters()
        .map((f) => this.labelFilters.find((lf) => lf.name === f))
        .filter(notEmpty);
      if (defaultFilter) {
        this.labelFiltersActive = defaultFilter;
      }
    },
    resetSearchAndFilters() {
      this.searchInput = '';
      this.applyDefaultFilters();
      this.showOutOfContextLabels = true;
      this.sortingOn = SortingOption.ConfidenceSuggestionName;

      labels.dispatch.saveActiveGlasses([]);
      this.glassesPanels = undefined;
      this.focusSearchInput(true);
    },
    isAnyGlassesActive() {
      return !!this.glasses.flatMap((v) => v.members).find((g) => g.isActive);
    },
    isGlassesGroupActive(group: string) {
      return this.glasses.find((v) => v.group === group)?.members.find((g) => g.isActive);
    },
    toggleGlasses(glasses: Glasses) {
      const activeGlasses = labels.state.activeGlasses;
      const glassesLabel = glasses.value;
      const exactExists = activeGlasses.find((v) => glassesLabel.type === v.type && glassesLabel.code === v.code);
      const typeExists = activeGlasses.find((v) => glassesLabel.type === v.type);
      let newActiveGlasses: Label[] = [];
      if (exactExists) {
        newActiveGlasses = activeGlasses.filter((v) => glassesLabel.type !== v.type || glassesLabel.code !== v.code);
      } else if (typeExists) {
        newActiveGlasses = [
          ...activeGlasses.filter((g) => g.type !== glassesLabel.type),
          glassesLabel,
        ];
      } else {
        newActiveGlasses = [
          ...activeGlasses,
          glassesLabel,
        ];
      }
      labels.dispatch.saveActiveGlasses(newActiveGlasses);
      // this.toggleGlassesPanels();
    },
    activeGlassesForGroup(group: string) {
      return this.glasses
        .filter((v) => v.group === group)
        ?.flatMap((g) => g.members)
        .filter((g) => g.isActive)
        .map((g) => g.value.code)
        .join(', ');
    },
    toggleGlassesPanels() {
      if (this.glassesPanels !== undefined) {
        this.glassesPanels = undefined;
      } else {
        this.glassesPanels = this.glasses.length === 1 ? [0] : this.glasses.map((v, idx) => idx);
      }
      this.focusSearchInput();
    },
    shortcuts(e: KeyboardEvent) {
      const shortcut = Array.from(AdvancedLabelTypeShortcuts).find(([_, key]) => e.code === key.code && e.altKey && e.shiftKey === (key?.shift ?? false));
      // ctrl + f -> focus the label search box
      if (e.key === 'f' && (e.ctrlKey || e.metaKey)) {
        this.focusSearchInput(true);
        e.preventDefault();
        return;
      }
      // alt + h
      if (e.key === 'h' && e.altKey) {
        this.resetSearchAndFilters();
        e.preventDefault();
      } else if (e.altKey && e.code >= 'Digit1' && e.code <= 'Digit9') {
        const number = parseInt(e.code.replace('Digit', ''), 10);
        const idx = number - 1;
        const saved = this.labelStatus.saved
          .map((la) => labels.getters.glasses.get(la.labelId))
          .filter(notEmpty)
          .filter((g) => getContextRelationTypes().includes(g.type))
          .sort(labelSorter(['type', 'length', 'alpha']));

        const added = this.labelStatus.added
          .map((la) => labels.getters.glasses.get(la.labelId))
          .filter(notEmpty)
          .filter((g) => getContextRelationTypes().includes(g.type));

        const relevant = [...saved, ...added];
        if (relevant[idx]) {
          // look for it in the glasses and toggle
          const labelId = relevant[idx].id;
          this.glasses.flatMap((v) => v.members).forEach((g) => {
            if (g.value.id === labelId) {
              this.toggleGlasses(g);
            }
          });
          e.preventDefault();
        }
      } else if (shortcut) {
        this.toggleFilter(shortcut[0], undefined);
        e.preventDefault();
      }
    },
    hasShortcut(filter: LabelFilter): boolean {
      return Array.from(AdvancedLabelTypeShortcuts).find(([name, _key]) => name === filter.name) !== undefined;
    },
    shortcutString(filter: LabelFilter): string {
      const shortcut = Array.from(AdvancedLabelTypeShortcuts).find(([name, _]) => name === filter.name);
      if (!shortcut) {
        return '';
      }
      const shift: boolean = shortcut[1]?.shift ?? false;
      return shortcut ? `Alt${shift ? ' + Shift' : ''} + ${this.keyCodeToLetter(shortcut[1].code)}` : '';
    },
    keyCodeToLetter(key: string): string {
      // KeyA -> A
      return key.replace('Key', '');
    },
  },
});

const compareSuggestionsByType = (
  type: LabelSuggestionType,
): (
    a: LabelWithStateAndSuggestions,
    b: LabelWithStateAndSuggestions,
  ) => number | undefined => (a, b) => {
  const suggestionA = findLabelSuggestion(a, type);
  const suggestionB = findLabelSuggestion(b, type);
  if (suggestionA && suggestionB) {
    if (isMlSuggestion(suggestionA)
      && isMlSuggestion(suggestionB)) {
      return suggestionB.confidence - suggestionA.confidence;
    } else {
      return a.code.localeCompare(b.code);
    }
  } else if (suggestionA) {
    return -1;
  } else if (suggestionB) {
    return 1;
  }
  return undefined;
};

const compareRelatedTo = (
): (
    a: LabelWithStateAndSuggestions,
    b: LabelWithStateAndSuggestions,
  ) => number | undefined => (a, b) => {
  if (a.relatedTo && b.relatedTo) {
    return b.relatedTo.length - a.relatedTo.length;
  } else if (a.relatedTo) {
    return -1;
  } else if (b.relatedTo) {
    return 1;
  }
  return undefined;
};

const compareNonSuggested = (
): (
    a: LabelWithStateAndSuggestions,
    b: LabelWithStateAndSuggestions,
  ) => number | undefined => (a, b) => {
  const aUnsuggested = a.suggestions?.length === 0;
  const bUnsuggested = b.suggestions?.length === 0;
  if (aUnsuggested && bUnsuggested) {
    return a.code.localeCompare(b.code);
  } else if (aUnsuggested) {
    return -1;
  } else if (bUnsuggested) {
    return 1;
  }
  return undefined;
};

const compareFuseScore = (
): (
    a: LabelWithStateAndSuggestions,
    b: LabelWithStateAndSuggestions,
  ) => number | undefined => (a, b) => {
  if (a.fuseScore && b.fuseScore) {
    return a.fuseScore - b.fuseScore;
  }
  return undefined;
};

const advancedLabelSorter = (a: LabelWithStateAndSuggestions, b: LabelWithStateAndSuggestions): number => {
  for (const sorter of [
    compareFuseScore(),
    compareSuggestionsByType('ingest'),
    compareSuggestionsByType('ml'),
    compareSuggestionsByType('related'),
    compareRelatedTo(),
    compareNonSuggested(),
    compareSuggestionsByType('outofcontext'),
  ]) {
    const result = sorter(a, b);
    if (result) return result;
  }
  return a.code.localeCompare(b.code);
};

